/**
 * The inline half of the Markdown converter: emphasis, code spans, links, images,
 * autolinks, line breaks and (optionally) raw HTML, following the CommonMark 0.31
 * algorithm — a delimiter stack for `*`, `_` and `~`, a bracket stack for links.
 */

import {
  characterAt,
  characterBefore,
  decodeReference,
  isAsciiPunctuation,
  isUnicodePunctuation,
  isUnicodeWhitespace,
  normalizeLabel,
  normalizeUri,
  unescapeString,
} from "@/lib/tool-kit/developer/markdown/text";

export type InlineType =
  | "root"
  | "text"
  | "softbreak"
  | "hardbreak"
  | "code"
  | "emph"
  | "strong"
  | "del"
  | "link"
  | "image"
  | "html";

export interface InlineNode {
  type: InlineType;
  literal: string;
  destination: string;
  title: string;
  parent: InlineNode | null;
  prev: InlineNode | null;
  next: InlineNode | null;
  firstChild: InlineNode | null;
  lastChild: InlineNode | null;
}

export interface LinkReference {
  destination: string;
  title: string;
}

export type ReferenceMap = Map<string, LinkReference>;

export interface InlineOptions {
  /** GitHub extensions: strikethrough and bare-URL autolinks. */
  gfm: boolean;
  /** Recognise raw HTML tags. Off means `<` is always text. */
  html: boolean;
}

export function createInline(type: InlineType, literal = ""): InlineNode {
  return {
    type,
    literal,
    destination: "",
    title: "",
    parent: null,
    prev: null,
    next: null,
    firstChild: null,
    lastChild: null,
  };
}

function unlink(node: InlineNode): void {
  if (node.prev) {
    node.prev.next = node.next;
  } else if (node.parent) {
    node.parent.firstChild = node.next;
  }
  if (node.next) {
    node.next.prev = node.prev;
  } else if (node.parent) {
    node.parent.lastChild = node.prev;
  }
  node.parent = null;
  node.prev = null;
  node.next = null;
}

function appendChild(parent: InlineNode, child: InlineNode): void {
  unlink(child);
  child.parent = parent;
  if (parent.lastChild) {
    parent.lastChild.next = child;
    child.prev = parent.lastChild;
    parent.lastChild = child;
  } else {
    parent.firstChild = child;
    parent.lastChild = child;
  }
}

function insertAfter(node: InlineNode, sibling: InlineNode): void {
  unlink(sibling);
  sibling.next = node.next;
  if (sibling.next) {
    sibling.next.prev = sibling;
  }
  sibling.prev = node;
  node.next = sibling;
  sibling.parent = node.parent;
  if (!sibling.next && sibling.parent) {
    sibling.parent.lastChild = sibling;
  }
}

/** Move every sibling after `start` (up to, not including, `end`) into `container`. */
function adoptSiblings(
  container: InlineNode,
  start: InlineNode,
  end: InlineNode | null
): void {
  let node = start.next;
  while (node && node !== end) {
    const next = node.next;
    appendChild(container, node);
    node = next;
  }
}

interface Delimiter {
  char: string;
  numdelims: number;
  origdelims: number;
  node: InlineNode;
  previous: Delimiter | null;
  next: Delimiter | null;
  canOpen: boolean;
  canClose: boolean;
}

interface Bracket {
  node: InlineNode;
  previous: Bracket | null;
  previousDelimiter: Delimiter | null;
  /** Where the `[` is in the subject. */
  index: number;
  image: boolean;
  active: boolean;
  bracketAfter: boolean;
}

/* Sticky patterns: each is tried at `lastIndex`, so no substring is ever copied. */
const RE_MAIN = /[^\n`[\]\\!<&*_~]+/y;
const RE_TICKS = /`+/y;
const RE_SPNL = /[ \t]*(?:\n[ \t]*)?/y;
const RE_LINK_LABEL = /\[(?:[^\\[\]]|\\[\s\S]){0,1000}\]/y;
const RE_DESTINATION_BRACES = /<(?:[^<>\n\\]|\\[\s\S])*>/y;
const RE_LINK_TITLE =
  /"(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*'|\((?:\\[\s\S]|[^()\\])*\)/y;
const RE_SPACE_TO_LINE_END = /[ \t]*(?:\n|$)/y;
const RE_EMAIL_AUTOLINK =
  /<([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)>/y;
const RE_URI_AUTOLINK = /<[A-Za-z][A-Za-z0-9.+-]{1,31}:[^<>\s]*>/y;
const RE_HTML_TAG =
  /<[A-Za-z][A-Za-z0-9-]*(?:\s+[a-zA-Z_:][a-zA-Z0-9_.:-]*(?:\s*=\s*(?:[^"'=<>`\s]+|'[^']*'|"[^"]*"))?)*\s*\/?>|<\/[A-Za-z][A-Za-z0-9-]*\s*>/y;
const RE_REFERENCE =
  /&(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});/y;
const RE_NEWLINES = /\n/g;
const RE_NON_SPACE = /[^ ]/;
const RE_TRAILING_SPACES = / +$/;
const RE_ASCII_LETTER = /^[A-Za-z]$/;
const RE_URL_START = /https?:\/\/|www\./g;

/** Characters after which a bare `www.` link may start (GFM). */
const WWW_PRECEDERS = new Set(["", "*", "_", "~", "("]);
const AUTOLINK_TRAILING = new Set([
  "?",
  "!",
  ".",
  ",",
  ":",
  "*",
  "_",
  "~",
  "'",
  '"',
]);
const LAST_ASCII_CONTROL = 0x20;
const MAX_LABEL_LENGTH = 1001;
/** cmark's limit: deeper nesting in a bare destination is not a link (and stays linear). */
const MAX_DESTINATION_PARENS = 32;

function matchAt(pattern: RegExp, subject: string, pos: number): string | null {
  pattern.lastIndex = pos;
  const match = pattern.exec(subject);
  return match ? match[0] : null;
}

function isHostCharacter(ch: string): boolean {
  return !(isUnicodeWhitespace(ch) || isUnicodePunctuation(ch));
}

/** Length of a valid GFM autolink domain at the start of `data`, or 0. */
function checkDomain(data: string, allowShort: boolean): number {
  if (data.length === 0) {
    return 0;
  }
  let dots = 0;
  let underscoresBefore = 0;
  let underscores = 0;
  let index = 1;
  for (; index < data.length - 1; index += 1) {
    const ch = data[index];
    if (ch === "_") {
      underscores += 1;
    } else if (ch === ".") {
      underscoresBefore = underscores;
      underscores = 0;
      dots += 1;
    } else if (!isHostCharacter(ch) && ch !== "-") {
      break;
    }
  }
  if (underscoresBefore > 0 || underscores > 0) {
    return 0;
  }
  if (allowShort) {
    return index;
  }
  return dots > 0 ? index : 0;
}

/** Trim trailing punctuation and unbalanced `)` off a GFM autolink, as cmark-gfm does. */
function trimAutolink(data: string, initialEnd: number): number {
  let end = initialEnd;
  let opening = 0;
  let closing = 0;
  for (let index = 0; index < end; index += 1) {
    if (data[index] === "(") {
      opening += 1;
    } else if (data[index] === ")") {
      closing += 1;
    }
  }
  while (end > 0) {
    const last = data[end - 1];
    if (last === ")") {
      if (closing <= opening) {
        return end;
      }
      closing -= 1;
      end -= 1;
    } else if (AUTOLINK_TRAILING.has(last)) {
      end -= 1;
    } else if (last === ";") {
      end = trimEntityLike(data, end);
    } else {
      return end;
    }
  }
  return end;
}

/** A trailing `&name;` is not part of a link; a lone `;` is just punctuation. */
function trimEntityLike(data: string, end: number): number {
  let start = end - 2;
  while (start > 0 && RE_ASCII_LETTER.test(data[start])) {
    start -= 1;
  }
  if (start < end - 2 && data[start] === "&") {
    return start;
  }
  return end - 1;
}

/** Where each run of N backticks starts, by N — so finding a closing run is a lookup. */
function indexBacktickRuns(subject: string): Map<number, number[]> {
  const runs = new Map<number, number[]>();
  let index = subject.indexOf("`");
  while (index !== -1) {
    let end = index;
    while (subject[end] === "`") {
      end += 1;
    }
    const starts = runs.get(end - index);
    if (starts) {
      starts.push(index);
    } else {
      runs.set(end - index, [index]);
    }
    index = subject.indexOf("`", end);
  }
  return runs;
}

/** First value in an ascending list that is at least `from`, or -1. */
function firstAtLeast(sorted: number[], from: number): number {
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (sorted[middle] < from) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low < sorted.length ? sorted[low] : -1;
}

/** Comments, processing instructions, CDATA and declarations: opener and terminator. */
const RAW_HTML_SPANS: readonly [string, string][] = [
  ["<!--", "-->"],
  ["<?", "?>"],
  ["<![CDATA[", "]]>"],
];

class InlineParser {
  subject = "";
  pos = 0;
  delimiters: Delimiter | null = null;
  brackets: Bracket | null = null;
  backtickRuns: Map<number, number[]> | null = null;
  readonly lastIndexes = new Map<string, number>();
  readonly refs: ReferenceMap;
  readonly options: InlineOptions;

  constructor(refs: ReferenceMap, options: InlineOptions) {
    this.refs = refs;
    this.options = options;
  }

  parse(text: string, root: InlineNode): void {
    this.subject = text;
    this.pos = 0;
    this.delimiters = null;
    this.brackets = null;
    this.backtickRuns = null;
    this.lastIndexes.clear();
    while (this.pos < this.subject.length) {
      this.parseInline(root);
    }
    this.processEmphasis(null);
  }

  parseInline(block: InlineNode): void {
    const c = this.subject[this.pos];
    let handled: boolean;
    switch (c) {
      case "\n":
        handled = this.parseNewline(block);
        break;
      case "\\":
        handled = this.parseBackslash(block);
        break;
      case "`":
        handled = this.parseBackticks(block);
        break;
      case "*":
      case "_":
        handled = this.handleDelim(c, block);
        break;
      case "~":
        handled = this.options.gfm && this.handleDelim(c, block);
        break;
      case "[":
        handled = this.parseOpenBracket(block);
        break;
      case "!":
        handled = this.parseBang(block);
        break;
      case "]":
        handled = this.parseCloseBracket(block);
        break;
      case "<":
        handled =
          this.parseAutolink(block) ||
          (this.options.html && this.parseHtmlTag(block));
        break;
      case "&":
        handled = this.parseEntity(block);
        break;
      default:
        handled = this.parseString(block);
    }
    if (!handled) {
      this.pos += 1;
      appendChild(block, createInline("text", c));
    }
  }

  parseNewline(block: InlineNode): boolean {
    this.pos += 1;
    const last = block.lastChild;
    if (last && last.type === "text" && last.literal.endsWith(" ")) {
      const hard = last.literal.endsWith("  ");
      last.literal = last.literal.replace(RE_TRAILING_SPACES, "");
      appendChild(block, createInline(hard ? "hardbreak" : "softbreak"));
    } else {
      appendChild(block, createInline("softbreak"));
    }
    while (this.subject[this.pos] === " ") {
      this.pos += 1;
    }
    return true;
  }

  parseBackslash(block: InlineNode): boolean {
    this.pos += 1;
    const next = this.subject[this.pos];
    if (next === "\n") {
      this.pos += 1;
      appendChild(block, createInline("hardbreak"));
    } else if (next !== undefined && isAsciiPunctuation(next)) {
      this.pos += 1;
      appendChild(block, createInline("text", next));
    } else {
      appendChild(block, createInline("text", "\\"));
    }
    return true;
  }

  parseBackticks(block: InlineNode): boolean {
    const ticks = matchAt(RE_TICKS, this.subject, this.pos) ?? "`";
    const afterOpen = this.pos + ticks.length;
    this.backtickRuns ??= indexBacktickRuns(this.subject);
    const close = firstAtLeast(
      this.backtickRuns.get(ticks.length) ?? [],
      afterOpen
    );
    if (close === -1) {
      this.pos = afterOpen;
      appendChild(block, createInline("text", ticks));
      return true;
    }
    let contents = this.subject
      .slice(afterOpen, close)
      .replace(RE_NEWLINES, " ");
    if (
      RE_NON_SPACE.test(contents) &&
      contents.startsWith(" ") &&
      contents.endsWith(" ")
    ) {
      contents = contents.slice(1, -1);
    }
    this.pos = close + ticks.length;
    appendChild(block, createInline("code", contents));
    return true;
  }

  /** Last index of `needle` in the subject, computed once per subject. */
  lastIndexOf(needle: string): number {
    let last = this.lastIndexes.get(needle);
    if (last === undefined) {
      last = this.subject.lastIndexOf(needle);
      this.lastIndexes.set(needle, last);
    }
    return last;
  }

  /** First `needle` at or after `from`; -1 without scanning when there is none. */
  indexAfter(needle: string, from: number): number {
    if (this.lastIndexOf(needle) < from) {
      return -1;
    }
    return this.subject.indexOf(needle, from);
  }

  scanDelims(c: string): {
    count: number;
    canOpen: boolean;
    canClose: boolean;
  } {
    let count = 0;
    while (this.subject[this.pos + count] === c) {
      count += 1;
    }
    const before = characterBefore(this.subject, this.pos);
    const after = characterAt(this.subject, this.pos + count);
    const afterSpace = isUnicodeWhitespace(after);
    const afterPunct = isUnicodePunctuation(after);
    const beforeSpace = isUnicodeWhitespace(before);
    const beforePunct = isUnicodePunctuation(before);
    const left = !afterSpace && (!afterPunct || beforeSpace || beforePunct);
    const right = !beforeSpace && (!beforePunct || afterSpace || afterPunct);
    if (c === "_") {
      return {
        count,
        canOpen: left && (!right || beforePunct),
        canClose: right && (!left || afterPunct),
      };
    }
    return { count, canOpen: left, canClose: right };
  }

  handleDelim(c: string, block: InlineNode): boolean {
    const scan = this.scanDelims(c);
    const start = this.pos;
    this.pos += scan.count;
    const node = createInline("text", this.subject.slice(start, this.pos));
    appendChild(block, node);
    // GFM: one or two tildes strike through; three or more are just text.
    const eligible = c !== "~" || scan.count <= 2;
    if (eligible && (scan.canOpen || scan.canClose)) {
      const delimiter: Delimiter = {
        char: c,
        numdelims: scan.count,
        origdelims: scan.count,
        node,
        previous: this.delimiters,
        next: null,
        canOpen: scan.canOpen,
        canClose: scan.canClose,
      };
      if (this.delimiters) {
        this.delimiters.next = delimiter;
      }
      this.delimiters = delimiter;
    }
    return true;
  }

  removeDelimiter(delimiter: Delimiter): void {
    if (delimiter.previous) {
      delimiter.previous.next = delimiter.next;
    }
    if (delimiter.next) {
      delimiter.next.previous = delimiter.previous;
    } else {
      this.delimiters = delimiter.previous;
    }
  }

  findOpener(
    closer: Delimiter,
    stackBottom: Delimiter | null,
    bottom: Delimiter | null
  ): Delimiter | null {
    let opener = closer.previous;
    while (opener !== null && opener !== stackBottom && opener !== bottom) {
      const oddMatch =
        (closer.canOpen || opener.canClose) &&
        closer.origdelims % 3 !== 0 &&
        (opener.origdelims + closer.origdelims) % 3 === 0;
      if (opener.char === closer.char && opener.canOpen && !oddMatch) {
        return opener;
      }
      opener = opener.previous;
    }
    return null;
  }

  /** Wrap what lies between two `*`/`_` runs in emphasis; returns the next closer. */
  closeEmphasis(opener: Delimiter, closer: Delimiter): Delimiter | null {
    const used = closer.numdelims >= 2 && opener.numdelims >= 2 ? 2 : 1;
    const openerNode = opener.node;
    const closerNode = closer.node;
    opener.numdelims -= used;
    closer.numdelims -= used;
    openerNode.literal = openerNode.literal.slice(0, -used);
    closerNode.literal = closerNode.literal.slice(0, -used);
    const emphasis = createInline(used === 1 ? "emph" : "strong");
    adoptSiblings(emphasis, openerNode, closerNode);
    insertAfter(openerNode, emphasis);
    // Nothing between the pair can match anything outside it any more.
    opener.next = closer;
    closer.previous = opener;
    if (opener.numdelims === 0) {
      unlink(openerNode);
      this.removeDelimiter(opener);
    }
    if (closer.numdelims === 0) {
      unlink(closerNode);
      const next = closer.next;
      this.removeDelimiter(closer);
      return next;
    }
    return closer;
  }

  /** GFM strikethrough: equal runs of one or two tildes. Unequal runs stay as text. */
  closeStrikethrough(opener: Delimiter, closer: Delimiter): Delimiter | null {
    const next = closer.next;
    if (opener.numdelims === closer.numdelims) {
      const struck = createInline("del");
      adoptSiblings(struck, opener.node, closer.node);
      insertAfter(opener.node, struck);
      unlink(opener.node);
      unlink(closer.node);
    }
    opener.next = closer;
    closer.previous = opener;
    this.removeDelimiter(closer);
    this.removeDelimiter(opener);
    return next;
  }

  processEmphasis(stackBottom: Delimiter | null): void {
    const openersBottom = new Map<string, Delimiter | null>();
    let closer = this.delimiters;
    while (closer !== null && closer.previous !== stackBottom) {
      closer = closer.previous;
    }
    while (closer !== null) {
      if (!closer.canClose) {
        closer = closer.next;
        continue;
      }
      const key = `${closer.char}${closer.canOpen ? 1 : 0}${closer.origdelims % 3}`;
      const bottom = openersBottom.has(key)
        ? (openersBottom.get(key) ?? null)
        : stackBottom;
      const opener = this.findOpener(closer, stackBottom, bottom);
      if (opener === null) {
        const oldCloser = closer;
        closer = closer.next;
        openersBottom.set(key, oldCloser.previous);
        if (!oldCloser.canOpen) {
          this.removeDelimiter(oldCloser);
        }
      } else if (closer.char === "~") {
        closer = this.closeStrikethrough(opener, closer);
      } else {
        closer = this.closeEmphasis(opener, closer);
      }
    }
    while (this.delimiters !== null && this.delimiters !== stackBottom) {
      this.removeDelimiter(this.delimiters);
    }
  }

  addBracket(node: InlineNode, index: number, image: boolean): void {
    if (this.brackets) {
      this.brackets.bracketAfter = true;
    }
    this.brackets = {
      node,
      previous: this.brackets,
      previousDelimiter: this.delimiters,
      index,
      image,
      active: true,
      bracketAfter: false,
    };
  }

  parseOpenBracket(block: InlineNode): boolean {
    const node = createInline("text", "[");
    appendChild(block, node);
    this.addBracket(node, this.pos, false);
    this.pos += 1;
    return true;
  }

  parseBang(block: InlineNode): boolean {
    if (this.subject[this.pos + 1] === "[") {
      const node = createInline("text", "![");
      appendChild(block, node);
      this.addBracket(node, this.pos + 1, true);
      this.pos += 2;
    } else {
      this.pos += 1;
      appendChild(block, createInline("text", "!"));
    }
    return true;
  }

  spnl(): void {
    this.pos += (matchAt(RE_SPNL, this.subject, this.pos) ?? "").length;
  }

  /** Length of a link label (`[…]`) at `pos`, or 0. */
  parseLinkLabel(): number {
    const label = matchAt(RE_LINK_LABEL, this.subject, this.pos);
    if (label === null || label.length > MAX_LABEL_LENGTH) {
      return 0;
    }
    return label.length;
  }

  parseLinkDestination(allowEmpty: boolean): string | null {
    const braced = matchAt(RE_DESTINATION_BRACES, this.subject, this.pos);
    if (braced !== null) {
      this.pos += braced.length;
      return normalizeUri(unescapeString(braced.slice(1, -1)));
    }
    if (this.subject[this.pos] === "<") {
      return null;
    }
    const start = this.pos;
    const end = this.scanBareDestination(start);
    const emptyAllowed = allowEmpty && this.subject[end] === ")";
    if (end === -1 || (end === start && !emptyAllowed)) {
      return null;
    }
    this.pos = end;
    return normalizeUri(unescapeString(this.subject.slice(start, end)));
  }

  /**
   * End of a destination written without angle brackets: no spaces or controls, and
   * parentheses balanced (at most 32 deep). -1 when they never balance.
   */
  scanBareDestination(start: number): number {
    let index = start;
    let depth = 0;
    while (index < this.subject.length) {
      const c = this.subject[index];
      if (c === "\\" && isAsciiPunctuation(this.subject[index + 1] ?? "")) {
        index += 2;
        continue;
      }
      if (c === ")" && depth === 0) {
        break;
      }
      if (c.charCodeAt(0) <= LAST_ASCII_CONTROL) {
        break;
      }
      if (c === "(") {
        depth += 1;
      } else if (c === ")") {
        depth -= 1;
      }
      if (depth > MAX_DESTINATION_PARENS) {
        return -1;
      }
      index += 1;
    }
    return depth === 0 ? index : -1;
  }

  parseLinkTitle(): string | null {
    const open = this.subject[this.pos];
    const close = open === "(" ? ")" : open;
    // An unclosed quote would otherwise be re-scanned to the end at every `](`.
    if (close === undefined || this.lastIndexOf(close) <= this.pos) {
      return null;
    }
    const title = matchAt(RE_LINK_TITLE, this.subject, this.pos);
    if (title === null) {
      return null;
    }
    this.pos += title.length;
    return unescapeString(title.slice(1, -1));
  }

  /** `(destination "title")` straight after the `]`. */
  parseInlineTarget(): LinkReference | null {
    const start = this.pos;
    if (this.subject[this.pos] !== "(") {
      return null;
    }
    this.pos += 1;
    this.spnl();
    const destination = this.parseLinkDestination(true);
    if (destination !== null) {
      this.spnl();
      let title = "";
      if (isUnicodeWhitespace(this.subject[this.pos - 1] ?? "")) {
        title = this.parseLinkTitle() ?? "";
      }
      this.spnl();
      if (this.subject[this.pos] === ")") {
        this.pos += 1;
        return { destination, title };
      }
    }
    this.pos = start;
    return null;
  }

  /** `[text][label]`, `[text][]` or `[text]`, resolved against the definitions. */
  parseReferenceTarget(
    opener: Bracket,
    afterClose: number
  ): LinkReference | null {
    const beforeLabel = this.pos;
    const labelLength = this.parseLinkLabel();
    let label = "";
    if (labelLength > 2) {
      label = this.subject.slice(beforeLabel, beforeLabel + labelLength);
      this.pos += labelLength;
    } else if (!opener.bracketAfter) {
      label = this.subject.slice(opener.index, afterClose);
      this.pos += labelLength;
    }
    if (
      label === "" ||
      label.length > MAX_LABEL_LENGTH ||
      this.refs.size === 0
    ) {
      return null;
    }
    return this.refs.get(normalizeLabel(label)) ?? null;
  }

  parseCloseBracket(block: InlineNode): boolean {
    this.pos += 1;
    const afterClose = this.pos;
    const opener = this.brackets;
    if (opener === null || !opener.active) {
      if (opener) {
        this.brackets = opener.previous;
      }
      appendChild(block, createInline("text", "]"));
      return true;
    }
    const target =
      this.parseInlineTarget() ?? this.parseReferenceTarget(opener, afterClose);
    if (target === null) {
      this.brackets = opener.previous;
      this.pos = afterClose;
      appendChild(block, createInline("text", "]"));
      return true;
    }
    const node = createInline(opener.image ? "image" : "link");
    node.destination = target.destination;
    node.title = target.title;
    adoptSiblings(node, opener.node, null);
    appendChild(block, node);
    this.processEmphasis(opener.previousDelimiter);
    this.brackets = opener.previous;
    unlink(opener.node);
    if (!opener.image) {
      // Links may not contain links: earlier `[` openers can no longer close one.
      let earlier = this.brackets;
      while (earlier !== null) {
        if (!earlier.image) {
          earlier.active = false;
        }
        earlier = earlier.previous;
      }
    }
    return true;
  }

  parseAutolink(block: InlineNode): boolean {
    const email = matchAt(RE_EMAIL_AUTOLINK, this.subject, this.pos);
    const uri = email ? null : matchAt(RE_URI_AUTOLINK, this.subject, this.pos);
    const found = email ?? uri;
    if (found === null) {
      return false;
    }
    this.pos += found.length;
    const address = found.slice(1, -1);
    const link = createInline("link");
    link.destination = normalizeUri(email ? `mailto:${address}` : address);
    appendChild(link, createInline("text", address));
    appendChild(block, link);
    return true;
  }

  /** Raw HTML at `pos`: a tag, comment, processing instruction, CDATA or declaration. */
  matchRawHtml(): string | null {
    const s = this.subject;
    const p = this.pos;
    for (const abrupt of ["<!-->", "<!--->"]) {
      if (s.startsWith(abrupt, p)) {
        return abrupt;
      }
    }
    for (const [opener, terminator] of RAW_HTML_SPANS) {
      if (s.startsWith(opener, p)) {
        const close = this.indexAfter(terminator, p + opener.length);
        return close === -1 ? null : s.slice(p, close + terminator.length);
      }
    }
    if (s.startsWith("<!", p) && RE_ASCII_LETTER.test(s[p + 2] ?? "")) {
      const close = this.indexAfter(">", p + 2);
      return close === -1 ? null : s.slice(p, close + 1);
    }
    return matchAt(RE_HTML_TAG, s, p);
  }

  parseHtmlTag(block: InlineNode): boolean {
    const tag = this.matchRawHtml();
    if (tag === null) {
      return false;
    }
    this.pos += tag.length;
    appendChild(block, createInline("html", tag));
    return true;
  }

  parseEntity(block: InlineNode): boolean {
    const reference = matchAt(RE_REFERENCE, this.subject, this.pos);
    if (reference === null) {
      return false;
    }
    this.pos += reference.length;
    appendChild(
      block,
      createInline("text", decodeReference(reference) ?? reference)
    );
    return true;
  }

  parseString(block: InlineNode): boolean {
    const run = matchAt(RE_MAIN, this.subject, this.pos);
    if (run === null) {
      return false;
    }
    if (
      this.options.gfm &&
      this.brackets === null &&
      this.parseRunWithAutolinks(block, run)
    ) {
      return true;
    }
    this.pos += run.length;
    appendChild(block, createInline("text", run));
    return true;
  }

  /**
   * GFM extended autolinks — `www.example.com` and `https://example.com` written as
   * plain text — in one pass over a run of ordinary text. Returns false (emitting
   * nothing) when the run has none, so the caller can emit it whole.
   */
  parseRunWithAutolinks(block: InlineNode, run: string): boolean {
    RE_URL_START.lastIndex = 0;
    let cursor = 0;
    let candidate = RE_URL_START.exec(run);
    while (candidate !== null && cursor < run.length) {
      const www = candidate[0] === "www.";
      const start = this.pos + candidate.index;
      const end = this.matchBareUrl(start, www);
      if (end > start) {
        if (candidate.index > cursor) {
          appendChild(
            block,
            createInline("text", run.slice(cursor, candidate.index))
          );
        }
        const text = this.subject.slice(start, end);
        const link = createInline("link");
        link.destination = normalizeUri(www ? `http://${text}` : text);
        appendChild(link, createInline("text", text));
        appendChild(block, link);
        cursor = end - this.pos;
        RE_URL_START.lastIndex = cursor;
      }
      candidate = RE_URL_START.exec(run);
    }
    if (cursor === 0) {
      return false;
    }
    if (cursor < run.length) {
      appendChild(block, createInline("text", run.slice(cursor)));
    }
    this.pos += Math.max(cursor, run.length);
    return true;
  }

  /** End of a bare URL starting at `start`, or `start` when there is none. */
  matchBareUrl(start: number, www: boolean): number {
    const before = characterBefore(this.subject, start);
    if (www) {
      if (!(WWW_PRECEDERS.has(before) || isUnicodeWhitespace(before))) {
        return start;
      }
    } else if (RE_ASCII_LETTER.test(before)) {
      return start;
    }
    const domainStart = www ? start : this.subject.indexOf("//", start) + 2;
    let lineEnd = domainStart;
    while (
      lineEnd < this.subject.length &&
      !isUnicodeWhitespace(this.subject[lineEnd]) &&
      this.subject[lineEnd] !== "<"
    ) {
      lineEnd += 1;
    }
    const domain = checkDomain(this.subject.slice(domainStart, lineEnd), !www);
    if (domain === 0) {
      return start;
    }
    return (
      start + trimAutolink(this.subject.slice(start, lineEnd), lineEnd - start)
    );
  }

  /** Read one link reference definition at `pos`; true when there was one. */
  parseReferenceAt(): boolean {
    const start = this.pos;
    const labelLength = this.parseLinkLabel();
    if (labelLength === 0 || this.subject[start + labelLength] !== ":") {
      return false;
    }
    const label = this.subject.slice(start, start + labelLength);
    this.pos = start + labelLength + 1;
    this.spnl();
    const destination = this.parseLinkDestination(false);
    if (destination === null) {
      return false;
    }
    const beforeTitle = this.pos;
    this.spnl();
    let title: string | null = null;
    if (this.pos !== beforeTitle) {
      title = this.parseLinkTitle();
    }
    if (title === null) {
      this.pos = beforeTitle;
    }
    let atLineEnd = this.matchLineEnd();
    if (!atLineEnd && title !== null) {
      title = null;
      this.pos = beforeTitle;
      atLineEnd = this.matchLineEnd();
    }
    const key = normalizeLabel(label);
    if (!atLineEnd || key === "") {
      return false;
    }
    if (!this.refs.has(key)) {
      this.refs.set(key, { destination, title: title ?? "" });
    }
    return true;
  }

  matchLineEnd(): boolean {
    const end = matchAt(RE_SPACE_TO_LINE_END, this.subject, this.pos);
    if (end === null) {
      return false;
    }
    this.pos += end.length;
    return true;
  }
}

/** Parse inline content into a tree under a `root` node. */
export function parseInlines(
  text: string,
  refs: ReferenceMap,
  options: InlineOptions
): InlineNode {
  const root = createInline("root");
  new InlineParser(refs, options).parse(text, root);
  return root;
}

/**
 * Strip the link reference definitions from the start of a paragraph's raw content,
 * recording each in `refs` (the first definition of a label wins). Returns what is left.
 */
export function extractReferences(content: string, refs: ReferenceMap): string {
  if (!content.startsWith("[")) {
    return content;
  }
  const parser = new InlineParser(refs, { gfm: false, html: false });
  parser.subject = content;
  let consumed = 0;
  while (content[consumed] === "[" && parser.parseReferenceAt()) {
    consumed = parser.pos;
  }
  return consumed === 0 ? content : content.slice(consumed);
}
